// --- START EDIT: Measure content height (not constrained item height) and include rowGap in span formula ---
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export interface UseMasonryGridOptions {
  /**
   * Base row height in pixels for the CSS grid-auto-rows
   * Default: 8
   */
  rowHeight?: number;

  /**
   * Debounce delay for window resize and observer events in milliseconds
   * Default: 100
   */
  debounceDelay?: number;

  /**
   * Optional selector to find the content wrapper inside each item.
   * If omitted, the hook will fallback to measuring the item's scrollHeight.
   * Example: '[data-masonry-content]'
   */
  contentSelector?: string;
}

export interface UseMasonryGridReturn {
  /**
   * Ref to attach to the grid container element
   */
  gridRef: React.RefObject<HTMLDivElement | null>;

  /**
   * Function to create a ref callback for individual grid items
   * @param key - Unique key for the grid item
   */
  setItemRef: (key: string) => (el: HTMLDivElement | null) => void;

  /**
   * Get the calculated row span for a specific item
   * @param key - Unique key for the grid item
   */
  getRowSpan: (key: string) => number;

  /**
   * Manually trigger a recalculation of all grid item spans
   * Useful when content changes (e.g., language switch)
   */
  recalculate: () => void;

  /**
   * Callback to attach to image onLoad events
   * Triggers a recalculation when images finish loading
   */
  handleImageLoad: () => void;
}

/**
 * A reusable hook for creating masonry-style grid layouts using CSS Grid and ResizeObserver.
 *
 * This hook calculates and applies grid-row-end spans to grid items based on their content height,
 * creating a masonry effect. It observes items for size changes and recalculates spans when needed.
 */
export function useMasonryGrid(options: UseMasonryGridOptions = {}): UseMasonryGridReturn {
  const { rowHeight = 8, debounceDelay = 100, contentSelector } = options;

  const gridRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [rowSpans, setRowSpans] = useState<Map<string, number>>(new Map());
  const rafIdRef = useRef<number | null>(null);
  const [itemCount, setItemCount] = useState(0);

  /**
   * Read the rowGap from the grid container (pixels)
   */
  const getRowGap = useCallback((): number => {
    const grid = gridRef.current;
    if (!grid) return 0;
    const gapStr = getComputedStyle(grid).rowGap;
    const gap = parseFloat(gapStr || "0");
    return Number.isFinite(gap) ? gap : 0;
  }, []);

  /**
   * Compute the span for a given content height.
   * Include rowGap so rows align cleanly in the masonry.
   */
  const computeSpan = useCallback(
    (contentHeight: number): number => {
      const rowGap = getRowGap();
      // Clamp at least 1 row
      const span = Math.max(1, Math.ceil((contentHeight + rowGap) / (rowHeight + rowGap)));
      return span;
    },
    [rowHeight, getRowGap]
  );

  /**
   * Determine the content height for an item.
   * Prefer measuring the inner content wrapper; fallback to item.scrollHeight.
   */
  const getItemContentHeight = useCallback(
    (item: HTMLDivElement): number => {
      if (!item) return 0;

      // Try contentSelector first if provided
      if (contentSelector) {
        const contentEl = item.querySelector<HTMLElement>(contentSelector);
        if (contentEl) {
          // Use scrollHeight (includes overflowing content) as the most stable signal
          return contentEl.scrollHeight || contentEl.getBoundingClientRect().height || 0;
        }
      }

      // Fallback: measure the item's total content height
      return item.scrollHeight || item.getBoundingClientRect().height || 0;
    },
    [contentSelector]
  );

  /**
   * Calculate row spans for all grid items
   * Uses requestAnimationFrame to batch DOM reads and writes
   */
  const measureRowSpans = useCallback(() => {
    // Cancel any pending RAF
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    // Schedule measurement in next animation frame
    rafIdRef.current = requestAnimationFrame(() => {
      const newSpans = new Map<string, number>();

      // Batch read all content heights first (avoid layout thrashing)
      const measurements: Array<{ key: string; contentHeight: number }> = [];
      itemRefs.current.forEach((item, key) => {
        if (item) {
          const contentHeight = getItemContentHeight(item);
          measurements.push({ key, contentHeight });
        }
      });

      // Then calculate spans
      for (const { key, contentHeight } of measurements) {
        const span = computeSpan(contentHeight);
        newSpans.set(key, span);
      }

      setRowSpans(newSpans);
      rafIdRef.current = null;
    });
  }, [computeSpan, getItemContentHeight]);

  /**
   * Create a ref callback for a grid item
   */
  const setItemRef = useCallback((key: string) => (el: HTMLDivElement | null) => {
    if (el) {
      itemRefs.current.set(key, el);
    } else {
      itemRefs.current.delete(key);
    }
    // Update item count to trigger ResizeObserver recreation
    setItemCount(itemRefs.current.size);
  }, []);

  /**
   * Get the row span for an item, defaulting to 1 if not calculated yet
   */
  const getRowSpan = useCallback(
    (key: string): number => {
      return rowSpans.get(key) || 1;
    },
    [rowSpans]
  );

  /**
   * Public API to manually trigger recalculation
   */
  const recalculate = useCallback(() => {
    measureRowSpans();
  }, [measureRowSpans]);

  /**
   * Callback for image load events
   */
  const handleImageLoad = useCallback(() => {
    measureRowSpans();
  }, [measureRowSpans]);

  // Measure on mount and when items change
  useLayoutEffect(() => {
    measureRowSpans();
  }, [measureRowSpans]);

  // Set up ResizeObserver to watch all grid items
  // Recreates observer when items are added/removed (itemCount changes)
  useEffect(() => {
    if (!gridRef.current) return;

    let timeoutId: number | null = null;

    const resizeObserver = new ResizeObserver(() => {
      // Debounce measurements to avoid excessive recalculations
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        measureRowSpans();
        timeoutId = null;
      }, debounceDelay);
    });

    // Observe all current items
    itemRefs.current.forEach((item) => {
      if (item) resizeObserver.observe(item);
    });

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      resizeObserver.disconnect();
    };
  }, [measureRowSpans, debounceDelay, itemCount]);

  // Handle window resize with debouncing
  useEffect(() => {
    let timeoutId: number | null = null;

    const handleResize = () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        measureRowSpans();
        timeoutId = null;
      }, debounceDelay);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [measureRowSpans, debounceDelay]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return {
    gridRef,
    setItemRef,
    getRowSpan,
    recalculate,
    handleImageLoad,
  };
}
// --- END EDIT: Measure content height (not constrained item height) and include rowGap in span formula ---